import { ManipulatedVariable, ReadinessScore, ProjectSummaryStats, CampaignSummaryStats, Subcontroller } from '../types';

export const TARGET_MOVES_PER_MV = 20;

export function calculateReadinessScore(validMoves: number, target: number = TARGET_MOVES_PER_MV): ReadinessScore {
  const safeTarget = target > 0 ? target : TARGET_MOVES_PER_MV;
  if (validMoves >= safeTarget) {
    return 'Ready for Modeling';
  } else if (validMoves >= Math.ceil(safeTarget * 0.5)) {
    return 'Adequate';
  } else {
    return 'Insufficient';
  }
}

export function calculateProgressPercentage(validMoves: number, target: number = TARGET_MOVES_PER_MV): number {
  const safeTarget = target > 0 ? target : TARGET_MOVES_PER_MV;
  return Math.min(100, Math.round((validMoves / safeTarget) * 100));
}

export function calculateDirectionalBalance(posMoves: number, negMoves: number): {
  ratioText: string;
  isBalanced: boolean;
  skew: 'Balanced' | 'Positive-Heavy' | 'Negative-Heavy';
} {
  const total = posMoves + negMoves;
  if (total === 0) {
    return { ratioText: '0 / 0', isBalanced: true, skew: 'Balanced' };
  }

  const diff = Math.abs(posMoves - negMoves);
  const isBalanced = diff <= 2;
  const skew = posMoves > negMoves + 2 ? 'Positive-Heavy' : negMoves > posMoves + 2 ? 'Negative-Heavy' : 'Balanced';

  return {
    ratioText: `+${posMoves} / -${negMoves}`,
    isBalanced,
    skew,
  };
}

export function computeProjectStats(
  projectName: string,
  mvs: ManipulatedVariable[],
  projectTargetMoves: number = TARGET_MOVES_PER_MV
): ProjectSummaryStats {
  const defaultTarget = projectTargetMoves > 0 ? projectTargetMoves : TARGET_MOVES_PER_MV;
  const totalMVs = mvs.length;
  const totalValidMoves = mvs.reduce((sum, m) => sum + m.validMoves, 0);
  
  // Calculate total target moves taking into account optional per-MV overrides or project default
  const targetMovesTotal = mvs.reduce((sum, m) => sum + (m.targetMoves || defaultTarget), 0);
  const remainingMoves = Math.max(0, targetMovesTotal - totalValidMoves);
  const projectProgressPct = targetMovesTotal > 0 ? Math.min(100, Math.round((totalValidMoves / targetMovesTotal) * 100)) : 0;

  const activeTestsCount = mvs.filter((m) => m.status === 'Active' || m.status === 'Waiting for Steady State').length;
  const rejectedTestsCount = mvs.reduce((sum, m) => sum + m.rejectedMoves, 0);

  const readyMvsCount = mvs.filter((m) => calculateReadinessScore(m.validMoves, m.targetMoves || defaultTarget) === 'Ready for Modeling').length;
  const overallModelReadinessPct = totalMVs > 0 ? Math.round((readyMvsCount / totalMVs) * 100) : 0;

  // Dynamically derive unique subcontrollers from MVs in the project
  const controllers: string[] = Array.from(
    new Set(mvs.map((m) => (m.controller || '').trim()).filter(Boolean))
  ).sort();
  const controllerStats: ProjectSummaryStats['controllerStats'] = {};

  for (const ctrl of controllers) {
    const ctrlMvs = mvs.filter((m) => m.controller === ctrl);
    const count = ctrlMvs.length;
    const valid = ctrlMvs.reduce((sum, m) => sum + m.validMoves, 0);
    const target = ctrlMvs.reduce((sum, m) => sum + (m.targetMoves || defaultTarget), 0);
    const ready = ctrlMvs.filter((m) => calculateReadinessScore(m.validMoves, m.targetMoves || defaultTarget) === 'Ready for Modeling').length;
    const adequate = ctrlMvs.filter((m) => calculateReadinessScore(m.validMoves, m.targetMoves || defaultTarget) === 'Adequate').length;
    const insufficient = ctrlMvs.filter((m) => calculateReadinessScore(m.validMoves, m.targetMoves || defaultTarget) === 'Insufficient').length;

    controllerStats[ctrl] = {
      totalMvs: count,
      validMoves: valid,
      targetMoves: target,
      readinessPct: target > 0 ? Math.min(100, Math.round((valid / target) * 100)) : 0,
      mvsReadyCount: ready,
      mvsAdequateCount: adequate,
      mvsInsufficientCount: insufficient,
    };
  }

  return {
    projectName,
    projectProgressPct,
    campaignName: projectName,
    campaignProgressPct: projectProgressPct,
    totalMVs,
    totalValidMoves,
    targetMovesTotal,
    remainingMoves,
    overallModelReadinessPct,
    activeTestsCount,
    rejectedTestsCount,
    controllerStats,
  };
}

export const computeCampaignStats = computeProjectStats;
